export const marketingLocales = ["en", "fr"] as const;

export type MarketingLocale = (typeof marketingLocales)[number];

export function isMarketingLocale(value: unknown): value is MarketingLocale {
  return typeof value === "string" && marketingLocales.includes(value as MarketingLocale);
}

export const marketingCopy = {
  en: {
    nav: {
      features: "Features",
      dashboard: "Dashboard",
      whiteLabel: "White-label",
      about: "About Us",
      requestDemo: "Request a Demo",
    },
    badge: "100% CANADIAN HOSTING",
    title: "Empower your candidates with resumes fully compliant with Canadian standards.",
    description:
      "A smart platform designed to streamline your teams’ workflow and maximize job seekers’ hiring success.",
    scheduleDemo: "Schedule a Demo",
    seeFeatures: "See the features",
    benefits: ["Official NOC code", "ATS-ready layouts", "EN & FR"],
    features: {
      eyebrow: "Platform features",
      title: "Designed to accelerate placement and boost advisor efficiency",
      items: [
        {
          title: "Automatic NOC Code",
          description:
            'Type the "Target Job Title" to instantly fetch the official NOC code, eliminating manual lookup.',
        },
        {
          title: "Canadian-Style CV Standards",
          description:
            "Convert existing resumes into layouts optimized for local recruiter expectations and Applicant Tracking Systems (ATS).",
        },
        {
          title: "AI-Assisted Writing",
          description:
            "Help your advisors and candidates generate high-impact, professional bullet points in seconds without starting from scratch.",
        },
        {
          title: "Bilingual Support",
          description:
            "Build a job-ready Canadian resume in English and French, fully adapted to your career goals.",
        },
        {
          title: "Canadian Data Sovereignty",
          description:
            "100% Canadian-hosted platform ensuring strict data privacy and security compliance for public and non-profit organizations.",
        },
        {
          title: "Advisor Time-Saver",
          description:
            "Step-by-step guided flow that lets candidates build high-quality CVs fast, saving your teams valuable coaching time.",
        },
      ],
    },
    orgBenefits: {
      eyebrow: "Benefits for your organization",
      title: "More candidates supported, with the same team",
      cards: [
        {
          title: "Time saved for advisors",
          description:
            "Candidates build their resume on their own with a guided flow. Advisors spend less time per resume and can support more candidates.",
        },
        {
          title: "Consistent Canadian compliance",
          description:
            "Every resume follows the same Canadian standards and is linked to an official NOC code, whoever the advisor.",
        },
        {
          title: "Tracking and reporting",
          description:
            "Follow resumes created, finalized and translated by period, and monitor licence usage in real time.",
        },
        {
          title: "Control over your data",
          description:
            "Data hosted 100% in Canada. Your organization chooses how long candidate files are kept, from 7 to 365 days depending on the plan.",
        },
      ],
      builtForLabel: "Built for",
      chips: [
        "Employment assistance organizations",
        "Immigrant settlement organizations",
        "Colleges and universities",
        "Public services",
        "Companies (outplacement, HR)",
      ],
      cta: {
        text: "See how your advisors can support more candidates without adding staff.",
        action: "Request a Demo",
      },
    },
    dashboard: {
      eyebrow: "Manager Dashboard",
      title: "Track usage and results in one place",
      checklist: [
        "Licence usage: resumes created against your plan, in real time",
        "Statistics by period: last 7 days, 30 days, this month or custom dates",
        "Advisor access: invite or revoke advisors from your workspace",
        "File tracking: reset AI regenerations or delete a resume when needed",
      ],
      preview: "Manager dashboard preview — licence usage and statistics",
      mockup: {
        url: "your-organization.cvbuilder.ca",
        title: "Dashboard",
        planLabel: "Annual Plan",
        planStatus: "Active",
        usageLabel: "Licence usage",
        usageBadge: "75% used",
        usageValue: "374",
        usageTotal: " / 500 resumes",
        statsLabel: "Statistics",
        periods: ["7 days", "30 days", "This month"],
        activePeriod: "30 days",
        stats: [
          { value: "184", label: "Resumes created" },
          { value: "146", label: "Resumes finalized" },
          { value: "97", label: "Translations generated" },
          { value: "231", label: "Regenerations used" },
        ],
      },
      cta: {
        text: "Open a candidate's resume, edit it, and send it back in minutes. See it live.",
        action: "Request a Demo",
      },
    },
    whiteLabel: {
      eyebrow: "White-label",
      title: "Your branding, your platform",
      description:
        "Deliver a trusted, seamless environment customized with your organization’s logo and official color palette.",
      prompt: "Want to see the platform in action?",
      action: "Schedule my demo",
      preview: "Sign-in page preview — client branding applied",
    },
    customProject: {
      eyebrow: "Custom Project",
      title: "Need something specific? We adapt CVBuilder to your programs.",
      action: "Discuss your project",
      cards: [
        {
          title: "Custom resume templates",
          description:
            "We design resume templates for your sectors or programs. Formatting stays automatic, so advisors never handle layout.",
        },
        {
          title: "Sign-in options",
          description:
            "Add more social media sign-in options or connect your organization's single sign-on (SSO).",
        },
        {
          title: "Retention period",
          description:
            "Candidate files kept for the period your policies require, from 7 to 365 days.",
        },
        {
          title: "Integrations and reports",
          description:
            "Connections with your tools and reports built around the indicators you track.",
        },
      ],
    },
    demoSection: {
      eyebrow: "Request a demo",
      title: "Ready to modernize your employment toolkit?",
      description:
        "Schedule a personalized demonstration with our team to discover how CVBuilder can equip your advisors and standardize resume creation.",
      contactLine: "Contact us for plans and pricing tailored to your organization.",
      benefits: [
        "Canadian compliance",
        "AI-assisted completion",
        "Time saved for your teams",
      ],
      formTitle: "Schedule Your Demo",
      fields: {
        fullName: "Full Name",
        fullNamePlaceholder: "Jane Tremblay",
        organization: "Organization Name",
        organizationPlaceholder: "Employment Services Centre",
        email: "Work Email",
        emailPlaceholder: "jane@organization.ca",
        phone: "Phone Number",
        phonePlaceholder: "(514) 000-0000",
        message: "How can we help your organization?",
        messagePlaceholder: "Tell us about your team and your needs",
        optional: "Optional",
      },
      submit: "Request a Demo",
      submitting: "Sending request...",
      success: "Thank you. Your demo request has been sent.",
      error: "We could not send your request. Please try again.",
    },
    jobSeekers: {
      eyebrow: "Candidate experience",
      title: "A guided 4-step flow",
      action: "Built My Resume",
      steps: [
        {
          title: "Profile & Import",
          description:
            "Upload an existing resume, enter your basic information, and link the official NOC/CNP code to your target job title.",
        },
        {
          title: "AI Analysis",
          description:
            "Our AI analyzes your resume to optimize your content and align it with Canadian standards.",
        },
        {
          title: "Review & Refine",
          description:
            "Easily adjust AI suggestions, fine-tune your details, and polish your bullet points.",
        },
        {
          title: "Download",
          description:
            "Instantly get your certified PDF resume, fully compliant and ready to submit to employers.",
        },
      ],
      cta: {
        text: "Walk through the candidate flow with our team.",
        action: "Request a Demo",
      },
    },
    about: {
      eyebrow: "About us",
      title: "Canadian technology built for the local employment ecosystem",
      description:
        "CVBuilder was created with a clear conviction: artificial intelligence should empower human expertise, not replace it. Our platform is purpose-built to meet the unique standards of the Canadian job market and support the vital mission of employment assistance organizations.",
      pillars: [
        {
          title: "Data Sovereignty",
          description:
            "Your organization’s data and your candidates’ privacy are 100% hosted in Canada with strict security compliance.",
        },
        {
          title: "Official Standards",
          description:
            "Automatically adds the official NOC code for the target job to each resume.",
        },
        {
          title: "Ethical, Human-First AI",
          description:
            "Designed to save advisors valuable coaching time and empower job seekers every step of the way.",
        },
        {
          title: "Eliminate formatting headaches",
          description:
            "Keep your focus on candidate content while our platform generates professional-grade layouts tailored to Canadian standards.",
        },
      ],
    },
    footer: {
      description:
        "The smart employability platform built for employment assistance organizations and job seekers across Canada.",
      hosting: "100% Canadian Data Hosting.",
      groups: [
        {
          title: "Organizations",
          links: [
            "Schedule a Demo",
            "Platform Features",
            "Automatic NOC Code",
            "Data Sovereignty",
          ],
        },
        {
          title: "Legal",
          links: ["Privacy Policy", "Terms of Service"],
        },
      ],
      contact: {
        title: "Contact Us",
        emailLabel: "Email: team@cvbuilder.ca",
        facebookLabel: "Facebook",
      },
      copyright: "© 2026 12sc. All rights reserved.",
    },
    mockup: {
      sections: "Sections",
      profile: "Profile",
      achievements: "Achievements",
      experience: "Professional Experience",
      education: "Education & Professional Development",
      additional: "Additional Information",
      complete: "82% complete",
      profileText: "Enthusiastic and creative Florist and Floral Design Student with 3+ years of practical experience in floral arrangement, plant care, event styling, and customer service across Colombia and Canada. Passionate about sustainable floristry, modern European and Nordic floral aesthetics, and creative event design. Reliable, detail-oriented, and fully bilingual in English and French, with native Spanish proficiency.",
      achievementItems: [
        "Reduced floral inventory waste by 15% through proper processing, conditioning, and care of incoming fresh flower shipments.",
        "Created custom floral designs for weddings, corporate events, and private functions in Bogotá.",
        "Developed hands-on expertise in the care and handling of delicate tropical and imported flowers.",
        "Supported customers throughout the purchasing process, from personalized floral orders to safe local delivery preparation.",
      ],
      experienceItems: [
        {
          company: "Elegance Florist",
          role: "Floral Assistant & Florist",
          location: "Montreal, QC, Canada",
          dates: "September 2025 – Present",
          details: [
            "Assemble daily floral bouquets, vase arrangements, and custom orders according to client specifications.",
            "Process, clean, and condition incoming fresh flower shipments to extend vase life and reduce inventory waste by 15%.",
            "Assist retail customers with floral selections and personalized orders.",
            "Process POS transactions and support daily store operations.",
            "Maintain clean, organized, and visually appealing floral displays.",
          ],
        },
        {
          company: "Rosas & Algo Más Florist",
          role: "Junior Florist & Event Assistant",
          location: "Bogotá, Colombia",
          dates: "January 2023 – July 2025",
          details: [
            "Designed custom bouquets and floral centrepieces for weddings, corporate events, and private functions.",
            "Maintained cold storage units and followed hydration and sanitization protocols for delicate tropical and imported flowers.",
            "Assisted with event setup and floral installation.",
            "Responded to customer inquiries through social media and supported order coordination.",
            "Prepared and safely packed floral orders for local courier delivery.",
          ],
        },
      ],
      educationItems: [
        { credential: "Diploma of Vocational Studies (DEP) – Floral Design / Art Floral", institution: "Montreal School of Horticultural Trades, Montreal, QC, Canada", dates: "2025 – Present" },
        { credential: "Technical Certificate in Floristry & Floral Arts", institution: "SENA – National Learning Service, Bogotá, Colombia", dates: "2023" },
      ],
      additionalItems: [
        "Floral Design: Hand-tied bouquets, wedding arrangements, sympathy flowers, centrepieces, colour theory, spiral technique",
        "Botanical Knowledge: Cut flower care, indoor plant maintenance, pest detection, cold-chain handling",
        "Customer service; POS systems; inventory management; event setup and teardown; floral order preparation and delivery",
        "Class 5 equivalent driver's license",
        "Spanish: Native (C2); English: Advanced / Fluent (C1) — IELTS General 7.5; French: Intermediate / Functional (B2) — Quebec French Learning Certificate",
        "Volunteer Florist Assistant, Montreal Botanical Garden — Fall 2025",
      ],
    },
  },
  fr: {
    nav: {
      features: "Fonctionnalités",
      dashboard: "Tableau de bord",
      whiteLabel: "Marque blanche",
      about: "À propos",
      requestDemo: "Demander une démo",
    },
    badge: "HÉBERGEMENT 100 % CANADIEN",
    title: "Donnez à vos candidats des CV entièrement conformes aux normes canadiennes.",
    description:
      "Une plateforme intelligente conçue pour simplifier le travail de vos équipes et maximiser la réussite professionnelle des chercheurs d’emploi.",
    scheduleDemo: "Planifier une démo",
    seeFeatures: "Voir les fonctionnalités",
    benefits: ["Code CNP officiel", "Mises en page compatibles ATS", "FR et EN"],
    features: {
      eyebrow: "Fonctionnalités de la plateforme",
      title: "Conçue pour accélérer le placement et améliorer l’efficacité des conseillers",
      items: [
        {
          title: "Code CNP automatique",
          description:
            "Saisissez le titre de l’emploi cible pour obtenir instantanément le code CNP officiel, sans recherche manuelle.",
        },
        {
          title: "Normes canadiennes de CV",
          description:
            "Adaptez facilement vos CV aux normes des recruteurs locaux et aux systèmes de suivi des candidatures (ATS).",
        },
        {
          title: "Rédaction assistée par l’IA",
          description:
            "Aidez les conseillers et les candidats à produire rapidement des réalisations professionnelles percutantes sans partir de zéro.",
        },
        {
          title: "Soutien bilingue",
          description:
            "Créez un CV canadien prêt à l'emploi, en anglais et en français, selon vos ambitions de carrière.",
        },
        {
          title: "Souveraineté des données canadiennes",
          description:
            "Une plateforme hébergée à 100 % au Canada qui respecte les exigences strictes de confidentialité et de sécurité des organismes publics et sans but lucratif.",
        },
        {
          title: "Gain de temps pour les conseillers",
          description:
            "Un parcours guidé étape par étape qui permet aux candidats de créer rapidement des CV de qualité et réduit le temps d’accompagnement.",
        },
      ],
    },
    orgBenefits: {
      eyebrow: "Avantages pour votre organisation",
      title: "Plus de candidats soutenus, avec la même équipe",
      cards: [
        {
          title: "Gain de temps pour les conseillers",
          description:
            "Les candidats créent leur CV de façon autonome grâce à un parcours guidé. Les conseillers consacrent moins de temps par CV et peuvent accompagner plus de candidats.",
        },
        {
          title: "Conformité canadienne uniforme",
          description:
            "Chaque CV respecte les mêmes normes canadiennes et est associé à un code CNP officiel, peu importe le conseiller.",
        },
        {
          title: "Suivi et rapports",
          description:
            "Suivez les CV créés, finalisés et traduits par période, et surveillez l’utilisation des licences en temps réel.",
        },
        {
          title: "Contrôle de vos données",
          description:
            "Données hébergées à 100 % au Canada. Votre organisation choisit la durée de conservation des dossiers des candidats, de 7 à 365 jours selon le forfait.",
        },
      ],
      builtForLabel: "Conçu pour",
      chips: [
        "Organismes d’aide à l’emploi",
        "Organismes d’aide à l’établissement des immigrants",
        "Collèges et universités",
        "Services publics",
        "Entreprises (reclassement, RH)",
      ],
      cta: {
        text: "Découvrez comment vos conseillers peuvent accompagner plus de candidats sans ajouter de personnel.",
        action: "Demander une démo",
      },
    },
    dashboard: {
      eyebrow: "Tableau de bord gestionnaire",
      title: "Suivez l’utilisation et les résultats en un seul endroit",
      checklist: [
        "Utilisation de la licence : CV créés par rapport à votre forfait, en temps réel",
        "Statistiques par période : 7 derniers jours, 30 jours, ce mois-ci ou dates personnalisées",
        "Accès des conseillers : invitez ou révoquez des conseillers de votre espace de travail",
        "Suivi des fichiers : réinitialisez les régénérations IA ou supprimez un CV au besoin",
      ],
      preview: "Aperçu du tableau de bord gestionnaire — utilisation de la licence et statistiques",
      mockup: {
        url: "votre-organisation.cvbuilder.ca",
        title: "Tableau de bord",
        planLabel: "Forfait annuel",
        planStatus: "Actif",
        usageLabel: "Utilisation de la licence",
        usageBadge: "75 % utilisé",
        usageValue: "374",
        usageTotal: " / 500 CV",
        statsLabel: "Statistiques",
        periods: ["7 jours", "30 jours", "Ce mois-ci"],
        activePeriod: "30 jours",
        stats: [
          { value: "184", label: "CV créés" },
          { value: "146", label: "CV finalisés" },
          { value: "97", label: "Traductions générées" },
          { value: "231", label: "Régénérations utilisées" },
        ],
      },
      cta: {
        text: "Ouvrez le CV d’un candidat, modifiez-le et renvoyez-le en quelques minutes. Voyez-le en direct.",
        action: "Demander une démo",
      },
    },
    whiteLabel: {
      eyebrow: "Marque blanche",
      title: "Votre image, votre plateforme",
      description:
        "Offrez une expérience fiable et harmonieuse, personnalisée avec le logo et la palette de couleurs officielle de votre organisation.",
      prompt: "Vous souhaitez voir la plateforme en action?",
      action: "Planifier ma démo",
      preview: "Aperçu de la page de connexion — image de marque du client appliquée",
    },
    customProject: {
      eyebrow: "Projet personnalisé",
      title: "Besoin de quelque chose de précis? Nous adaptons CVBuilder à vos programmes.",
      action: "Discutons de votre projet",
      cards: [
        {
          title: "Modèles de CV personnalisés",
          description:
            "Nous concevons des modèles de CV pour vos secteurs ou programmes. La mise en page reste automatique, les conseillers n’ont jamais à s’en occuper.",
        },
        {
          title: "Options de connexion",
          description:
            "Ajoutez d’autres options de connexion via les réseaux sociaux ou connectez l’authentification unique (SSO) de votre organisation.",
        },
        {
          title: "Durée de conservation",
          description:
            "Dossiers des candidats conservés selon la durée exigée par vos politiques, de 7 à 365 jours.",
        },
        {
          title: "Intégrations et rapports",
          description:
            "Connexions avec vos outils et rapports conçus autour des indicateurs que vous suivez.",
        },
      ],
    },
    demoSection: {
      eyebrow: "Demander une démonstration",
      title: "Prêt à moderniser votre trousse d’outils en employabilité?",
      description:
        "Planifiez une démonstration personnalisée avec notre équipe pour découvrir comment CVBuilder peut outiller vos conseillers et uniformiser la création de CV.",
      contactLine:
        "Communiquez avec nous pour connaître les forfaits et les tarifs adaptés à votre organisation.",
      benefits: [
        "Conformité canadienne",
        "Rédaction assistée par l’IA",
        "Gain de temps pour vos équipes",
      ],
      formTitle: "Planifiez votre démonstration",
      fields: {
        fullName: "Nom complet",
        fullNamePlaceholder: "Jeanne Tremblay",
        organization: "Nom de l’organisation",
        organizationPlaceholder: "Centre de services d’emploi",
        email: "Courriel professionnel",
        emailPlaceholder: "jeanne@organisation.ca",
        phone: "Numéro de téléphone",
        phonePlaceholder: "(514) 000-0000",
        message: "Comment pouvons-nous aider votre organisation?",
        messagePlaceholder: "Parlez-nous de votre équipe et de vos besoins",
        optional: "Facultatif",
      },
      submit: "Demander une démo",
      submitting: "Envoi de la demande...",
      success: "Merci. Votre demande de démonstration a été envoyée.",
      error: "Nous n’avons pas pu envoyer votre demande. Veuillez réessayer.",
    },
    jobSeekers: {
      eyebrow: "Expérience candidat",
      title: "Un parcours guidé en 4 étapes",
      action: "Créer mon CV",
      steps: [
        {
          title: "Profil et importation",
          description:
            "Téléversez un CV existant, saisissez vos renseignements de base et associez le code CNP officiel à l’emploi ciblé.",
        },
        {
          title: "Analyse par l’IA",
          description:
            "Notre IA analyse votre CV afin d’optimiser son contenu et de l’harmoniser avec les normes canadiennes.",
        },
        {
          title: "Révision et amélioration",
          description:
            "Ajustez facilement les suggestions de l’IA, précisez vos renseignements et améliorez vos réalisations.",
        },
        {
          title: "Téléchargement",
          description:
            "Obtenez instantanément votre CV certifié en PDF, conforme et prêt à être transmis aux employeurs.",
        },
      ],
      cta: {
        text: "Parcourez le cheminement du candidat avec notre équipe.",
        action: "Demander une démo",
      },
    },
    about: {
      eyebrow: "À propos",
      title: "Une technologie canadienne conçue pour l’écosystème local de l’emploi",
      description:
        "CVBuilder est né d’une conviction claire : l’intelligence artificielle doit renforcer l’expertise humaine, et non la remplacer. Notre plateforme est conçue spécialement pour répondre aux normes uniques du marché du travail canadien et soutenir la mission essentielle des organismes d’aide à l’emploi.",
      pillars: [
        {
          title: "Souveraineté des données",
          description:
            "Les données de votre organisation et la vie privée de vos candidats sont protégées par un hébergement entièrement canadien et des normes de sécurité strictes.",
        },
        {
          title: "Normes officielles",
          description:
            "Associe automatiquement le bon code CNP officiel à chaque CV selon l’emploi visé.",
        },
        {
          title: "Une IA éthique et centrée sur l’humain",
          description:
            "Conçue pour faire gagner un temps précieux aux conseillers et soutenir les chercheurs d’emploi à chaque étape.",
        },
        {
          title: "Finis les casse-têtes de mise en page",
          description:
            "Concentrez-vous sur le contenu des candidats pendant que notre plateforme génère des mises en page professionnelles adaptées aux normes canadiennes.",
        },
      ],
    },
    footer: {
      description:
        "La plateforme intelligente d’employabilité conçue pour les organismes d’aide à l’emploi et les chercheurs d’emploi partout au Canada.",
      hosting: "Hébergement des données 100 % canadien.",
      groups: [
        {
          title: "Organisations",
          links: [
            "Planifier une démo",
            "Fonctionnalités de la plateforme",
            "Code CNP automatique",
            "Souveraineté des données",
          ],
        },
        {
          title: "Juridique",
          links: ["Politique de confidentialité", "Conditions d’utilisation"],
        },
      ],
      contact: {
        title: "Nous contacter",
        emailLabel: "Courriel : team@cvbuilder.ca",
        facebookLabel: "Facebook",
      },
      copyright: "© 2026 12sc. Tous droits réservés.",
    },
    mockup: {
      sections: "Sections",
      profile: "Profil",
      achievements: "Réalisations",
      experience: "Expérience professionnelle",
      education: "Formation et perfectionnement professionnel",
      additional: "Informations supplémentaires",
      complete: "82 % terminé",
      profileText: "Créative, rigoureuse et passionnée, je suis une fleuriste et étudiante en art floral cumulant plus de 3 ans d'expérience pratique en Colombie et au Canada. Spécialisée en entretien des plantes, composition florale, service à la clientèle et stylisme événementiel, je m'inspire grandement de l'esthétique moderne européenne et nordique tout en promouvant des pratiques florales durables. Parfaitement trilingue (anglais, français et espagnol), je fais preuve d'une excellente fiabilité dans la réalisation de projets événementiels sur mesure.",
      achievementItems: [
        "Optimisation du soin, de la préparation et du conditionnement des arrivages de fleurs fraîches, permettant de diminuer le gaspillage floral de 15 %.",
        "Conception d'arrangements floraux personnalisés destinés aux réceptions privées, mariages et événements corporatifs à Bogotá.",
        "Prise en charge complète de la clientèle, depuis la prise de commande sur mesure jusqu'à l'organisation sécurisée des livraisons locales.",
        "Savoir-faire éprouvé dans la manipulation ainsi que le suivi méticuleux des variétés florales délicates, tropicales ou importées.",
      ],
      experienceItems: [
        {
          company: "Fleuriste Élégance",
          role: "Assistant(e) floral(e) et fleuriste",
          location: "Montréal, QC, Canada",
          dates: "Septembre 2025 – Aujourd'hui",
          details: [
            "Création sur mesure de bouquets et d'arrangements floraux adaptés aux besoins spécifiques de chaque client.",
            "Nettoyage, traitement et préparation rigoureux des fleurs fraîches pour la livraison, prolongeant la tenue en vase et réduisant les pertes de 15 %.",
            "Accompagnement et conseil personnalisé dans la sélection des variétés florales.",
            "Gestion des encaissements et contribution active au bon déroulement des activités en boutique.",
            "Entretien et mise en valeur des présentations florales en magasin pour garantir un espace accueillant et soigné.",
          ],
        },
        {
          company: "Rosas & Algo Más Florist",
          role: "Fleuriste Junior & Assistante Événementielle",
          location: "Bogotá, Colombie",
          dates: "Janvier 2023 – Juillet 2025",
          details: [
            "Création de bouquets et de centres de table floraux sur mesure pour mariages, événements corporatifs et réceptions privées.",
            "Gestion des chambres froides et respect des protocoles d'hydratation et de désinfection pour les fleurs tropicales et importées délicates.",
            "Aide à la mise en place des événements et à l'installation florale.",
            "Réponse aux demandes des clients via les réseaux sociaux et soutien à la coordination des commandes.",
            "Préparation et emballage sécurisé des commandes florales pour livraison par coursier local.",
          ],
        },
      ],
      educationItems: [
        { credential: "Diplôme d'études professionnelles (DEP) – Design floral / Art floral", institution: "École des métiers horticoles de Montréal, Montréal (Québec), Canada", dates: "2025 – Aujourd'hui" },
        { credential: "Certificat technique en art floral", institution: "SENA – Service national d'apprentissage, Bogotá, Colombie", dates: "2023" },
      ],
      additionalItems: [
        "Design & art floral : Création de bouquets champêtres, compositions pour mariages, fleurs de deuil et centres de table; maîtrise de la théorie des couleurs et de la technique de la spirale",
        "Horticulture & botanique : Soin des fleurs coupées, entretien des plantes d’intérieur, détection des parasites et manipulation à froid",
        "Gestion & opérations : Service à la clientèle, systèmes de point de vente, gestion des stocks, préparation et livraison des commandes de fleurs",
        "Événementiel : Montage, installation et démontage de décors floraux",
        "Compétences linguistiques : Espagnol (langue maternelle, C2), Anglais (courant/avancé, C1 – IELTS Général 7,5), Français (intermédiaire/fonctionnel, B2)",
        "Engagement communautaire : Assistant fleuriste bénévole au Jardin botanique de Montréal (Automne 2025); Permis de conduire : Classe 5 ou équivalent",
      ],
    },
  },
} as const satisfies Record<MarketingLocale, object>;
